import SpaInfoBoxGenerated, { SpaInfoBoxGeneratedEntityManager } from './generated/SpaInfoBox.generated';
import { BusinessEntityFieldType } from '@xFrame4/business/base/Constants';
import { imageInfoGraphQlDetails } from '@xFrame4/business/Constants';
import { YouTubeVideoInfo, ImageInfo } from '@xFrame4/business/Types';
import ImageGallery, { GalleryImage } from '@xFrame4/business/images/ImageGallery';
import { SpaInfoBoxTranslation } from './SpaInfoBoxTranslation';
import BusinessEntityWithOrderNoHelper from '@xFrame4/business/base/BusinessEntityWithOrderNoHelper';
import { BusinessEntityFilter } from '@xFrame4/business/base/BusinessEntity';


export class SpaInfoBoxEntityManager extends SpaInfoBoxGeneratedEntityManager<SpaInfoBoxTranslation, SpaInfoBox>
{
    constructor()
    {
        super({
            createEntity: () => new SpaInfoBox(),
            fields: [
                { name: 'image', type: BusinessEntityFieldType.Custom, inputProperty: 'imageFile', customGraphQl: imageInfoGraphQlDetails },
                { name: 'hasImage', type: BusinessEntityFieldType.Boolean, isInput: false },
                { name: 'galleryImages', type: BusinessEntityFieldType.Custom, isInput: false, customGraphQl: ImageGallery.graphQlGalleryImageFieldDetails },
                { 
                    name: 'attachments', 
                    type: BusinessEntityFieldType.Custom, 
                    isInput: false, 
                    customGraphQl: `
                        id
                        fileName
                        name
                        description
                        isActive
                        url
                        translations {
                            language
                            name
                            description
                        }
                    ` 
                }
            ]
        });
    }
}

/**
 * SpaInfoBox
 * 
 * Read this document on how to work with TypeScript business entities: .cursor/rules/xapp-ts-business-entity-rules.mdc
 */
export class SpaInfoBox extends SpaInfoBoxGenerated<SpaInfoBoxTranslation>
{
    static _manager: SpaInfoBoxEntityManager;
    static get manager(): SpaInfoBoxEntityManager
    {
        if (!this._manager) this._manager = new SpaInfoBoxEntityManager();
        return this._manager;
    }

    /** The image info for the entity. */
    image!: ImageInfo;
    /** Whether the entity has an image file stored in the media folder. */
    hasImage: boolean = false;
    /** The image file object to use for uploading. */
    imageFile?: File;
    /** The image gallery manager for the entity. */
    imageGallery: ImageGallery;
    /** The gallery images for the entity. */
    galleryImages: GalleryImage[] = [];
    /** The attachments for the entity. */
    attachments: any[] = [];

    constructor()
    {
        super();
        this.imageGallery = new ImageGallery(this, 'SpaInfoBoxImageGallery');
    }

    // Override
    copyFromGraphQL(graphQlObject: any)
    {
        super.copyFromGraphQL(graphQlObject);

        // Load gallery images
        if (graphQlObject.galleryImages != null)
        {
            this.imageGallery.loadImagesFromGraphQl(graphQlObject.galleryImages);
        }

        // Load attachments
        if (graphQlObject.attachments != null)
        {
            this.attachments = graphQlObject.attachments;
        }
    }

    // Optional: override the toJson method to skip the gallery images (useful for Next.js)
    toJson()
    {
        return super.toJson(['imageGallery']);
    }

    /**
     * Move the spa info box to a new position.
     * 
     * @param targetObject The target object to move the spa info box to.
     * @param before If true, the spa info box will be moved before the target object. If false, the spa info box will be moved after the target object.
     */
    async moveTo(targetObject: SpaInfoBox, before: boolean = true)
    {
        return BusinessEntityWithOrderNoHelper.moveTo('SpaInfoBox', this, targetObject, before);
    }

    /**
     * Load a light version of a spa info box.
     * Used for detail pages where we need most fields but can optimize gallery image sizes.
     * 
     * @param filter Optional filter to narrow down the results
     */
    static async loadLight(filter?: BusinessEntityFilter)
    {
        let query = `
            id
            orderNo
            isActive
            showOpeningHours
            showPrices
            showSpaMenu
            translations {
                language
                title
                shortDescription
                description
                descriptionExtra
                slug
            }
            galleryImages {
                id
                orderNo
                isDefault
                original { ${imageInfoGraphQlDetails} }
                thumbnail { ${imageInfoGraphQlDetails} }
                veryLarge169 { ${imageInfoGraphQlDetails} }
                large169 { ${imageInfoGraphQlDetails} }
            },
            attachments {
                id
                fileName
                name
                description
                isActive
                url
                translations {
                    language
                    name
                    description
                }
            }
        `;

        return await this.manager.loadCustom(query, filter);
    }

    /**
     * Load spa info boxes with minimal fields.
     * Used for listing pages where we only need basic information.
     * 
     * @param filter Optional filter to narrow down the results
     */
    static async loadVeryLight(filter?: BusinessEntityFilter)
    {
        let query = `
            id
            orderNo
            showOpeningHours
            showPrices
            translations {
                language
                title
                shortDescription
                slug
            }
            galleryImages (isDefault: true) {
                id
                orderNo
                isDefault
                medium { ${imageInfoGraphQlDetails} }
            }
        `;

        return await this.manager.loadCustom(query, filter);
    }

    /**
     * Get a very light version of a spa info box for navigation and routing.
     * 
     * @param language The translation language
     * @param slug The slug for the language
     */
    static async getVeryLightForNavigation(language: string, slug: string)
    {
        let query = `
            id
            translations {
                language
                slug
                title
                shortDescription
                seoTitle
                seoDescription
                seoKeywords
            }
        `;

        let result = await this.manager.loadCustom(query, {
            translation_Language: language,
            translation_Slug: slug
        });

        return result?.entities[0] as SpaInfoBox | null;
    }
}


export default SpaInfoBox;